import React, { useState, useRef, useEffect } from 'react';
import style from './componentCss/NavbarSimplified.module.css';
import { GrLanguage } from 'react-icons/gr';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/LogoWithName.svg';
import { FiMenu, FiX } from 'react-icons/fi';

function Navbar() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [langPopupOpen, setLangPopupOpen] = useState(false);
    const languageContainerRef = useRef(null);

    // Fermer popup langue si clic en dehors (seulement desktop)
    useEffect(() => {
        if (menuOpen) return; // pas sur mobile
        const handleClickOutside = (event) => {
            if (
                languageContainerRef.current &&
                !languageContainerRef.current.contains(event.target)
            ) {
                setLangPopupOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    // Fermer menu burger si resize au dessus du mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) setMenuOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navMenu = [
        { menu: t("Navbar.HomeTab"), url: '' },
        { menu: t("Navbar.OurProjects"), url: '#ourProjects' },
        { menu: t("Navbar.ConnectedDevices"), url: 'Connected-Devices' },
        { menu: t("Navbar.WebApps"), url: 'Web-Apps' },
        { menu: t("Navbar.Contact"), url: 'Contact' }
    ];

    const availableLanguages = [
        { code: 'fr', label: 'Français', flag: `${process.env.PUBLIC_URL}/img/Flags/fr.png` },
        { code: 'en', label: 'English', flag: `${process.env.PUBLIC_URL}/img/Flags/en.png` },
        { code: 'de', label: 'Deutsch', flag: `${process.env.PUBLIC_URL}/img/Flags/de.png` },
        { code: 'es', label: 'Español', flag: `${process.env.PUBLIC_URL}/img/Flags/es.png` },
        { code: 'it', label: 'Italiano', flag: `${process.env.PUBLIC_URL}/img/Flags/it.png` }
    ];

    const handleNavClick = (url) => {
        setMenuOpen(false); // ferme le menu burger sur mobile
        setLangPopupOpen(false);
        if (url === '') {
            location.pathname === '/' ? window.scrollTo({ top: 0, behavior: 'smooth' }) : navigate('/');
        } else if (url.startsWith('#')) {
            if (location.pathname === '/') {
                const target = document.querySelector(url);
                if (target) {
                    const yOffset = -90;
                    const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            } else {
                navigate('/');
                setTimeout(() => {
                    const target = document.querySelector(url);
                    if (target) {
                        const yOffset = -90;
                        const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                }, 400);
            }
        } else {
            navigate(`/${url.toLowerCase()}`);
        }
    };

    const toggleLangPopup = (e) => {
        e.stopPropagation();
        setLangPopupOpen(prev => !prev);
    };

    const changeLanguage = (lng) => {
        // Mémorise le choix utilisateur pour ne pas réécraser par l'auto-détection
        try { window.localStorage.setItem('i18nextLng', lng); } catch {}
        i18n.changeLanguage(lng);
        setLangPopupOpen(false);
        setMenuOpen(false);
    };

    // On détecte la largeur de l'écran pour afficher la langue dans le menu burger uniquement en mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <nav className={style.container}>
            {/* LOGO */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => handleNavClick('')}
                onKeyDown={e => { if (e.key === 'Enter') handleNavClick(''); }}
                className={style.logoContainer}
                style={{ cursor: 'pointer' }}
            >
                <Logo className={style.logo} />
            </div>

            {/* DROITE DE LA NAVBAR */}
            <div className={style.rightGroup}>
                {/* NAVIGATION : desktop = visible, mobile = caché */}
                <div className={`${style.navLinks} ${menuOpen ? style.navOpen : ''}`}>
                    {navMenu.map((navItem, index) => (
                        <span
                            key={index}
                            className={style.navItem}
                            onClick={() => handleNavClick(navItem.url)}
                        >
                            {navItem.menu}
                        </span>
                    ))}
                    {/* === GLOBE LANGUE === */}
                    {isMobile && menuOpen && (
                        <div className={style.languageMobile}>
                            <div
                                className={style.languageMobileContainer}
                                ref={languageContainerRef}
                                onClick={toggleLangPopup}
                                aria-haspopup="true"
                                aria-expanded={langPopupOpen}
                            >
                                <GrLanguage className={style.languageSelector} />
                                {langPopupOpen && (
                                    <div className={style.popup} role="menu">
                                        {availableLanguages.map(({ code, label, flag }) => (
                                            <div
                                                key={code}
                                                className={style.popupItem}
                                                role="menuitem"
                                                onClick={(e) => { e.stopPropagation(); changeLanguage(code); }}
                                                tabIndex={0}
                                                onKeyDown={e => { if (e.key === 'Enter') changeLanguage(code); }}
                                            >
                                                <img
                                                    src={flag}
                                                    alt={`${label} flag`}
                                                    className={style.popupFlag}
                                                    loading="lazy"
                                                />
                                                {label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>

                {/* LANGUE (desktop uniquement) */}
                {!isMobile && (
                    <div
                        className={style.languageContainer}
                        ref={languageContainerRef}
                        onClick={toggleLangPopup}
                        aria-haspopup="true"
                        aria-expanded={langPopupOpen}
                    >
                        <GrLanguage className={style.languageSelector} />
                        {langPopupOpen && (
                            <div className={style.popup} role="menu">
                                {availableLanguages.map(({ code, label, flag }) => (
                                    <div
                                        key={code}
                                        className={style.popupItem}
                                        role="menuitem"
                                        onClick={(e) => { e.stopPropagation(); changeLanguage(code); }}
                                        tabIndex={0}
                                        onKeyDown={e => { if (e.key === 'Enter') changeLanguage(code); }}
                                    >
                                        <img
                                            src={flag}
                                            alt={`${label} flag`}
                                            className={style.popupFlag}
                                            loading="lazy"
                                        />
                                        {label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* BURGER ICON : visible que sur mobile */}
                <div
                    className={style.burgerIcon}
                    onClick={() => setMenuOpen(prev => !prev)}
                    aria-label="Ouvrir le menu"
                >
                    {menuOpen ? <FiX size={28} color="white" /> : <FiMenu size={28} color="white" />}
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
